/** @format */

import { Print } from '@mui/icons-material';
import { Box, Container, IconButton, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import useMaxDate from '../BizBarometr/hooks/useMaxDate';
import PrintMetaDetails from '../BizBarometr/components/header/PrintMetaDetails';
import PrintHotelName from '../../Atoms/PrintHotelName';
import bizBarometrLogo from '../../../Assets/img/BizBarometr_Logo.png';
import { FILTER_NAMES, useAppContext } from '../../../store/store.app';
import { isCYHotelSelected } from '../../Library/appfilter.lib';

function InfoHeader() {
  const maxDate = useMaxDate();

  const {
    appStore: { appFilters },
  } = useAppContext();

  const selectedDateRange = appFilters[FILTER_NAMES.DATERANGE]?.[0];
  const selectedEndDate =
    isCYHotelSelected(appFilters) && selectedDateRange?.endDateCY
      ? selectedDateRange.endDateCY
      : selectedDateRange?.endDate;

  const reportDate = selectedEndDate ? dayjs(selectedEndDate) : maxDate;

  const printPDF = () => {
    const printStyles = `
      @media print {
        @page {
          size: A4 landscape;
          margin: 0 !important;
        }

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          background: #fff !important;
        }

        body * {
          visibility: hidden !important;
        }

        .MuiContainer-maxWidthXl {
          visibility: visible !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          padding: 12px 16px 16px !important;
          margin: 0 !important;
        }

        .MuiContainer-maxWidthXl * {
          visibility: visible !important;
        }

        .flash-print-header {
          min-height: 34px !important;
          padding-top: 4px !important;
          box-sizing: border-box !important;
        }

        .print-report-title {
          position: relative !important;
          width: 100% !important;
          text-align: center !important;
          font-size: 22px !important;
          line-height: 1.2 !important;
          margin: 0 0 1px 0 !important;
        }

        .print-inline-logo {
          display: block !important;
          position: absolute !important;
          left: 0 !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          width: 125px !important;
          height: auto !important;
        }

        .no-print,
        [aria-label*="Print"] {
          display: none !important;
        }

        .flash-report-rev-budget {
          break-before: page !important;
          page-break-before: always !important;
        }

        .MuiBox-root {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .MuiPaper-root {
          margin-bottom: 16px;
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .echarts-for-react,
        .echarts-container {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      }
    `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = printStyles;
    styleSheet.id = 'print-styles';

    document.head.appendChild(styleSheet);

    const printButton = document.querySelector('[aria-label*="Print"]');

    if (printButton) {
      printButton.classList.add('no-print');
    }

    window.print();

    setTimeout(() => {
      const existingStyle = document.getElementById('print-styles');

      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }

      if (printButton) {
        printButton.classList.remove('no-print');
      }
    }, 100);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        px: { xs: 0, sm: 3 },

        mt: { xs: 2, sm: 0 },
      }}
    >
      <Box
        className="flash-print-header"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
          flexWrap: 'wrap',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'none',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            fontSize={12}
            color="text.secondary"
            sx={{
              fontFamily: '"Public Sans", sans-serif',
            }}
          >
            Last Update: as of Business Date:{' '}
            {reportDate ? reportDate.format('DD/MM/YYYY') : ''}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: 0,
            zIndex: 1,
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Tooltip title="Print as PDF">
            <IconButton aria-label="Print" onClick={printPDF}>
              <Print fontSize="small" htmlColor="gray" />
            </IconButton>
          </Tooltip>
        </Box>

        <PrintMetaDetails />
      </Box>

      <Typography
        className="print-report-title"
        sx={{
          position: 'relative',
          textAlign: 'left',
          fontSize: { xs: '17px', sm: '22px' },
          color: '#697180',
          fontWeight: 700,
          textTransform: 'uppercase',
          fontFamily: '"Public Sans", sans-serif',
          lineHeight: 1.2,
          margin: 0,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src={bizBarometrLogo}
          alt="BizBarometr"
          className="print-inline-logo"
          sx={{
            display: 'none',
            '@media print': {
              display: 'block',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 125,
              height: 'auto',
            },
          }}
        />
        <PrintHotelName />
        Flash - {reportDate ? reportDate.format('DD/MM/YYYY') : ''}
      </Typography>
    </Container>
  );
}

export default InfoHeader;
